import { createElement, lazy, Suspense } from 'react';

const loadable = (
  importFunc,
  { fallback: FallbackComponent = null } = { fallback: null },
) => {
  const LazyComponent = lazy(importFunc);

  return props =>
    createElement(
      Suspense,
      {
        fallback: FallbackComponent ? createElement(FallbackComponent) : null,
      },
      createElement(LazyComponent, props),
    );
};

const isChunkLoadError = error => {
  return (
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.name === 'ChunkLoadError' ||
    error?.code === 'ERR_MODULE_NOT_FOUND'
  );
};

const hasReloadedForChunkError = () => {
  return sessionStorage.getItem('chunkErrorReloaded') === 'true';
};

const markChunkErrorReloaded = () => {
  sessionStorage.setItem('chunkErrorReloaded', 'true');
};

export const retry = (fn, retriesLeft = 3, baseInterval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        if (retriesLeft === 1) {
          if (isChunkLoadError(error) && !hasReloadedForChunkError()) {
            markChunkErrorReloaded();
            window.location.reload();
            return;
          }
          reject(error);
          return;
        }

        const attemptNumber = 2 - retriesLeft;
        const exponentialDelay = baseInterval * Math.pow(2, attemptNumber);
        
        setTimeout(() => {
          retry(fn, retriesLeft - 1, baseInterval).then(resolve, reject);
        }, exponentialDelay);
      });
  });
};

export const lazyWithRetry = (importFunc, retriesLeft = 3, baseInterval = 1000) => {
  return lazy(() => retry(importFunc, retriesLeft, baseInterval));
};

export default loadable;
